import { useState, useEffect, useCallback } from 'react'
import { shuffle } from '../utils'

export type Difficulty = 'easy' | 'medium' | 'hard'
export type GameMode   = 'synonyms' | 'antonyms' | 'mixed'
export type QuestionType = 'synonym' | 'antonym'

export interface SynonymEntry {
  id: number
  es: { word: string; synonyms: string[]; antonyms: string[] }
  en: { word: string; synonyms: string[]; antonyms: string[] }
}

export const QUESTIONS_PER_GAME = 10

// ── Easy: 4 MCQ options, 1 correct
// ── Medium: 10-word shared pool matching game
// ── Hard: 10 options, multiple correct (all synonyms/antonyms of the word)

export interface Question {
  entry:        SynonymEntry
  questionType: QuestionType
  correctWords: string[]   // all valid correct answers
  options:      string[]   // displayed options (shuffled)
}

export interface SynonymsState {
  questions:        Question[]
  // easy + hard: index into questions[]
  currentIdx:       number
  // medium: remaining question indices to solve, in queue order
  questionQueue:    number[]
  // medium: indices that have been answered
  answered:         Set<number>
  // medium: shared pool of 10 words (one correct per question)
  pool:             string[]
  // feedback
  correctFlashWord: string | null   // easy: green flash on correct option
  allCorrectFlash:  boolean         // hard: all-correct flash before advance
  wrongFlashWord:   string | null   // any mode: red flash on wrong
  // hard multi-select
  selectedWords:    Set<string>
  moves:            number
  startTime:        number | null
  endTime:          number | null
  gameComplete:     boolean
}

// ── Pure helpers ─────────────────────────────────────────────

function pickWords(entry: SynonymEntry, lang: 'es' | 'en'): { word: string; synonyms: string[]; antonyms: string[] } {
  return entry[lang]
}

function buildEasyOptions(correct: string, wrongPool: string[]): string[] {
  const distractors = shuffle(wrongPool.filter(w => w !== correct)).slice(0, 3)
  return shuffle([correct, ...distractors])
}

function buildHardOptions(correctWords: string[], wrongPool: string[]): string[] {
  const needed   = Math.max(0, 10 - correctWords.length)
  const distractors = shuffle(wrongPool.filter(w => !correctWords.includes(w))).slice(0, needed)
  return shuffle([...correctWords, ...distractors])
}

function resolveQuestionType(mode: GameMode, idx: number, mixedTypes: QuestionType[]): QuestionType {
  if (mode === 'synonyms') return 'synonym'
  if (mode === 'antonyms') return 'antonym'
  return mixedTypes[idx]
}

function buildQuestions(
  entries: SynonymEntry[],
  difficulty: Difficulty,
  mode: GameMode,
  lang: 'es' | 'en',
): Question[] {
  const count   = Math.min(QUESTIONS_PER_GAME, entries.length)
  const picked  = shuffle([...entries]).slice(0, count)
  const mixedTypes: QuestionType[] = shuffle(
    Array.from({ length: count }, (_, i) => (i < Math.ceil(count / 2) ? 'synonym' : 'antonym') as QuestionType),
  )

  return picked.map((entry, idx) => {
    const lv          = pickWords(entry, lang)
    const questionType = resolveQuestionType(mode, idx, mixedTypes)
    const correctWords = questionType === 'synonym' ? lv.synonyms : lv.antonyms

    // Distractor pool: all words from OTHER entries
    const otherWords = entries
      .filter(e => e.id !== entry.id)
      .flatMap(e => {
        const ov = e[lang]
        return [ov.word, ...ov.synonyms, ...ov.antonyms]
      })

    let options: string[]
    if (difficulty === 'easy') {
      options = buildEasyOptions(correctWords[0], otherWords)
    } else if (difficulty === 'hard') {
      options = buildHardOptions(correctWords, otherWords)
    } else {
      // medium: options are built from the shared pool (set later)
      options = []
    }

    return { entry, questionType, correctWords, options }
  })
}

function buildMediumPool(questions: Question[]): string[] {
  // One correct word per question (first synonym/antonym), shuffled
  return shuffle(questions.map(q => q.correctWords[0]))
}

export function initState(
  entries: SynonymEntry[],
  difficulty: Difficulty,
  mode: GameMode,
  lang: 'es' | 'en',
): SynonymsState {
  if (entries.length === 0) {
    return {
      questions: [], currentIdx: 0, questionQueue: [], answered: new Set(), pool: [],
      correctFlashWord: null, allCorrectFlash: false, wrongFlashWord: null,
      selectedWords: new Set(), moves: 0, startTime: null, endTime: null, gameComplete: false,
    }
  }

  const questions = buildQuestions(entries, difficulty, mode, lang)
  const pool      = difficulty === 'medium' ? buildMediumPool(questions) : []
  const questionQueue = difficulty === 'medium'
    ? Array.from({ length: questions.length }, (_, i) => i)
    : []

  return {
    questions,
    currentIdx:       0,
    questionQueue,
    answered:         new Set(),
    pool,
    correctFlashWord: null,
    allCorrectFlash:  false,
    wrongFlashWord:   null,
    selectedWords:    new Set(),
    moves:            0,
    startTime:        null,
    endTime:          null,
    gameComplete:     false,
  }
}

// ── Hook ─────────────────────────────────────────────────────

export function useSynonyms(
  entries: SynonymEntry[],
  difficulty: Difficulty,
  mode: GameMode,
  lang: 'es' | 'en',
) {
  const [state, setState] = useState<SynonymsState>(() =>
    initState(entries, difficulty, mode, lang),
  )

  // Re-init when entries arrive (async load) or params change
  useEffect(() => {
    setState(initState(entries, difficulty, mode, lang))
  }, [entries, difficulty, mode, lang])

  const resetGame = useCallback(() => {
    setState(initState(entries, difficulty, mode, lang))
  }, [entries, difficulty, mode, lang])

  // Easy: advance after correct-flash (700ms)
  useEffect(() => {
    if (state.correctFlashWord === null) return
    const t = setTimeout(() => {
      setState(prev => {
        const nextIdx = prev.currentIdx + 1
        if (nextIdx >= prev.questions.length) {
          return { ...prev, correctFlashWord: null, gameComplete: true, endTime: Date.now() }
        }
        return { ...prev, currentIdx: nextIdx, correctFlashWord: null }
      })
    }, 700)
    return () => clearTimeout(t)
  }, [state.correctFlashWord])

  // Hard: advance after all-correct flash (700ms)
  useEffect(() => {
    if (!state.allCorrectFlash) return
    const t = setTimeout(() => {
      setState(prev => {
        const nextIdx = prev.currentIdx + 1
        if (nextIdx >= prev.questions.length) {
          return { ...prev, allCorrectFlash: false, selectedWords: new Set(), gameComplete: true, endTime: Date.now() }
        }
        return { ...prev, allCorrectFlash: false, selectedWords: new Set(), currentIdx: nextIdx }
      })
    }, 700)
    return () => clearTimeout(t)
  }, [state.allCorrectFlash])

  // Clear wrong flash after 650ms
  useEffect(() => {
    if (state.wrongFlashWord === null) return
    const t = setTimeout(() => {
      setState(prev => ({ ...prev, wrongFlashWord: null }))
    }, 650)
    return () => clearTimeout(t)
  }, [state.wrongFlashWord])

  // ── Easy mode: tap one of 4 options ─────────────────────────
  const selectEasyOption = useCallback((word: string) => {
    setState(prev => {
      if (prev.correctFlashWord !== null || prev.gameComplete) return prev
      const q         = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (q.correctWords.includes(word)) {
        return { ...prev, moves, startTime, correctFlashWord: word }
      }
      return { ...prev, moves, startTime, wrongFlashWord: word }
    })
  }, [])

  // ── Medium mode: tap a word from the pool ───────────────────
  const selectPoolWord = useCallback((word: string) => {
    setState(prev => {
      if (prev.gameComplete) return prev
      const qIdx      = prev.questionQueue[0]
      const q         = prev.questions[qIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (q.correctWords.includes(word)) {
        // Remove from pool, mark answered, advance queue
        const newPool     = prev.pool.filter(w => w !== word)
        const newAnswered = new Set(prev.answered)
        newAnswered.add(qIdx)
        const newQueue    = prev.questionQueue.slice(1)
        const gameComplete = newQueue.length === 0
        return {
          ...prev, moves, startTime, pool: newPool, answered: newAnswered,
          questionQueue: newQueue, gameComplete, endTime: gameComplete ? Date.now() : null,
        }
      }
      return { ...prev, moves, startTime, wrongFlashWord: word }
    })
  }, [])

  // ── Medium mode: skip current question ──────────────────────
  const skipQuestion = useCallback(() => {
    setState(prev => {
      if (prev.questionQueue.length <= 1 || prev.gameComplete) return prev
      // Move head to tail
      const [head, ...rest] = prev.questionQueue
      return { ...prev, questionQueue: [...rest, head] }
    })
  }, [])

  // ── Hard mode: toggle a word selection ──────────────────────
  const toggleHardWord = useCallback((word: string) => {
    setState(prev => {
      if (prev.allCorrectFlash || prev.gameComplete) return prev
      const q         = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1

      const selected  = new Set(prev.selectedWords)
      if (selected.has(word)) {
        selected.delete(word)
        return { ...prev, selectedWords: selected }
      }

      if (!q.correctWords.includes(word)) {
        // Wrong selection
        selected.add(word)
        return { ...prev, moves, startTime, selectedWords: selected, wrongFlashWord: word }
      }

      selected.add(word)
      // Check if all correct words are now selected
      const allSelected = q.correctWords.every(w => selected.has(w))
      if (allSelected) {
        return { ...prev, moves, startTime, selectedWords: selected, allCorrectFlash: true }
      }
      return { ...prev, moves, startTime, selectedWords: selected }
    })
  }, [])

  return {
    state,
    resetGame,
    selectEasyOption,
    selectPoolWord,
    skipQuestion,
    toggleHardWord,
  }
}
